import { Spinner } from '@latitude/spinner';
import { cn } from '@latitude/token-utils';
import { groupTypes } from 'components/items';
import { ToggleGroupItemButton } from 'components/items/button';
import { ToggleGroupItemDatepicker } from 'components/items/datepicker';
import { ToggleGroupItemSelect } from 'components/items/select';
import Separator from 'components/items/separator';
import { useCallback, useMemo } from 'react';
import type { ToggleGroupProps } from 'types';

import { toggleGroupVariants } from './variants/toggle-group-variants';

/**
 * ToggleGroup component renders a group of toggle items.
 * @param {ToggleGroupProps} props - The props for the component.
 * @returns {JSX.Element} The ToggleGroup component.
 */
const ToggleGroup = <T,>({
  className,
  items,
  components,
  selected,
  type,
  variant = 'default',
  changeItemSelectToSelected = false,
  analyticsFn,
  loading,
  ...props
}: ToggleGroupProps<T>) => {
  const analytics = useCallback(
    (eventName: string, payload?: object) => {
      analyticsFn?.(eventName, payload);
    },
    [analyticsFn],
  );

  const toggleGroupItems = useMemo(() => {
    const componentsToUse = { ...groupTypes, ...components };
    return items.flatMap((item, index) => {
      const { component: ItemComponent, ...itemComponentProps } =
        componentsToUse[type];

      const itemSelected = selected.find(
        (selectedItem) => selectedItem.id === item.id,
      );

      const items = [];
      if (item.type === 'button') {
        items.push(
          <ToggleGroupItemButton
            key={item.id}
            componentTriggerType={type}
            Component={ItemComponent}
            item={item}
            analytics={analytics}
            selected={itemSelected}
            {...itemComponentProps}
          />,
        );
      }

      if (item.type === 'select') {
        items.push(
          <ToggleGroupItemSelect
            key={item.id}
            componentTriggerType={type}
            Component={ItemComponent}
            item={item}
            analytics={analytics}
            selected={itemSelected}
            changeItemSelectToSelected={changeItemSelectToSelected}
            {...itemComponentProps}
          />,
        );
      }

      if (item.type === 'datepicker') {
        items.push(
          <ToggleGroupItemDatepicker
            key={item.id}
            componentTriggerType={type}
            Component={ItemComponent}
            item={item}
            analytics={analytics}
            selected={itemSelected}
            {...itemComponentProps}
          />,
        );
      }

      if (item.hasSeparator) {
        items.push(
          <Separator
            type={item.type}
            index={index}
            key={`separator-${index}`}
          />,
        );
      }

      return items;
    });
  }, [
    analytics,
    changeItemSelectToSelected,
    components,
    items,
    selected,
    type,
  ]);

  const containerClasses = cn(
    toggleGroupVariants({
      variant,
    }),
    className,
  );

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className={containerClasses} {...props}>
      {toggleGroupItems}
    </div>
  );
};

ToggleGroup.displayName = 'ToggleGroup';

export {
  type ButtonItem,
  type DatepickerItem as DateItem,
  type SelectItem,
  type ToggleGroupComponentItem,
  type ToggleGroupItem,
  type ToggleGroupItemBase,
  type ToggleGroupItemType,
  type ToggleGroupProps,
  type ToggleGroupSelectOption,
  type ToggleGroupSelectedValue,
  type ToggleGroupType,
  type ToggleGroupVariant,
} from 'types';

export { ToggleGroup };
